import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CheckIcon, Loader2Icon } from 'lucide-react';

import { useLanguage } from '@/contexts/language-context';
import { Button } from '@/components/ui/button';
import { listTiers, TIERS_QUERY_KEY, type TierDto } from '@/lib/api/tiers';
import type { TierId } from '@/types';

const KNOWN_TIER_SLUGS: readonly TierId[] = ['solo', 'team', 'pro'];

function isKnownTierSlug(slug: string): slug is TierId {
  return (KNOWN_TIER_SLUGS as readonly string[]).includes(slug);
}

// repo.Tier only gives a slug, label, and price — the feature comparison
// copy below (users/businessPage/paymentFees/analytics) isn't modeled by
// the backend, so it stays client-side, keyed by the known marketing slugs.
// A tier whose slug isn't one of these still renders (label + live price),
// just without the comparison rows.
function formatMonthlyPrice(cents: number) {
  const dollars = cents / 100;
  return `$${dollars % 1 === 0 ? dollars.toFixed(0) : dollars.toFixed(2)}`;
}

function TierCard({
  tier,
  selected,
  onSelect,
}: {
  tier: TierDto;
  selected: boolean;
  onSelect: () => void;
}) {
  const { t } = useLanguage();
  const copy = isKnownTierSlug(tier.slug) ? t.tiers[tier.slug] : null;
  const recommended = tier.slug === 'team';
  const features = [t.scheduling, t.invoices, t.quotes, t.messaging];

  return (
    <button
      type="button"
      onClick={onSelect}
      className={[
        'relative flex flex-col gap-3 rounded-xl border p-5 text-left transition-all w-full',
        selected
          ? 'border-primary bg-primary/5 ring-2 ring-primary/30'
          : 'border-border bg-card hover:border-primary/50',
      ].join(' ')}
    >
      {recommended && (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-xs font-semibold px-3 py-0.5 rounded-full">
          {t.recommended}
        </span>
      )}

      <div className="flex items-baseline justify-between">
        <span className="text-lg font-bold">{copy?.name ?? tier.label}</span>
        <span className="text-2xl font-bold">
          {formatMonthlyPrice(tier.monthly_price_cents)}
          <span className="text-sm font-normal text-muted-foreground">{t.perMonth}</span>
        </span>
      </div>

      {copy && (
        <div className="flex flex-col gap-1 text-sm text-muted-foreground">
          <span>
            <span className="font-medium text-foreground">{t.users}:</span> {copy.users}
          </span>
          <span>
            <span className="font-medium text-foreground">{t.customers}:</span> {t.unlimited}
          </span>
          <span>
            <span className="font-medium text-foreground">{t.businessPage}:</span> {copy.businessPage}
          </span>
          <span>
            <span className="font-medium text-foreground">{t.paymentFees}:</span> {copy.paymentFees}
          </span>
          <span>
            <span className="font-medium text-foreground">{t.analytics}:</span> {copy.analytics}
          </span>
        </div>
      )}

      <ul className="flex flex-wrap gap-1.5 mt-1">
        {features.map((f) => (
          <li
            key={f}
            className="flex items-center gap-1 bg-muted rounded-full px-2.5 py-0.5 text-xs font-medium"
          >
            <CheckIcon className="w-3 h-3 text-primary" />
            {f}
          </li>
        ))}
      </ul>

      {selected && (
        <div className="absolute top-3 right-3 w-5 h-5 rounded-full bg-primary flex items-center justify-center">
          <CheckIcon className="w-3 h-3 text-primary-foreground" />
        </div>
      )}
    </button>
  );
}

export function StepTier({
  defaultTierId,
  onNext,
  onBack,
  submitting,
}: {
  // Real id from GET /tiers (repo.Tier.id), not the marketing-copy slug.
  defaultTierId: string;
  onNext: (tierId: string) => void;
  onBack: () => void;
  submitting: boolean;
}) {
  const { t } = useLanguage();
  const [selected, setSelected] = useState(defaultTierId);

  // Static lookup of subscription tiers — see Task 4d in the integration
  // plan. Cached under TIERS_QUERY_KEY for the session.
  const tiersQuery = useQuery({
    queryKey: TIERS_QUERY_KEY,
    queryFn: listTiers,
  });

  const activeTiers = (tiersQuery.data ?? []).filter((tier) => tier.is_active);

  // Default the selection once tiers load: keep an existing choice if it's
  // still valid, otherwise prefer the "team" slug, otherwise the first
  // active tier.
  useEffect(() => {
    if (!tiersQuery.data) return;
    if (selected && activeTiers.some((tier) => tier.id === selected)) return;
    const fallback =
      activeTiers.find((tier) => tier.slug === 'team') ?? activeTiers[0];
    if (fallback) setSelected(fallback.id);
    // Intentionally re-runs only when the fetched tier list changes — using
    // `selected`/`activeTiers` would re-trigger this on every selection.
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [tiersQuery.data]);

  return (
    <div className="flex flex-col gap-5">
      {tiersQuery.isPending && (
        <div className="flex items-center justify-center py-10 text-muted-foreground">
          <Loader2Icon className="w-5 h-5 animate-spin" />
        </div>
      )}

      {tiersQuery.isError && (
        <p className="text-destructive text-sm text-center py-6">
          Could not load plans. Please try again.
        </p>
      )}

      {!tiersQuery.isPending && !tiersQuery.isError && (
        <div className="flex flex-col gap-3">
          {activeTiers.map((tier) => (
            <TierCard
              key={tier.id}
              tier={tier}
              selected={selected === tier.id}
              onSelect={() => setSelected(tier.id)}
            />
          ))}
        </div>
      )}

      <div className="flex gap-3 mt-2">
        <Button type="button" variant="outline" className="flex-1" onClick={onBack}>
          {t.back}
        </Button>
        <Button
          type="button"
          className="flex-1"
          onClick={() => onNext(selected)}
          disabled={submitting || !selected}
        >
          {submitting && <Loader2Icon className="animate-spin w-4 h-4" />}
          {t.complete}
        </Button>
      </div>
    </div>
  );
}
